import { useState } from 'react'

export default function VideoEmbed({ url, videoUrl, posterUrl, autoLoad = false }) {
  const [active, setActive] = useState(autoLoad)

  // Vídeo enviado (upload S3) tem prioridade sobre o link externo — player nativo,
  // sem carregar iframe/SDK de terceiros, para não pesar o catálogo.
  if (videoUrl) {
    if (!active) {
      return (
        <div
          className="relative w-full cursor-pointer group"
          style={{ paddingBottom: '56.25%' }}
          onClick={() => setActive(true)}
        >
          {posterUrl ? (
            <img
              src={posterUrl}
              alt="Vídeo do lote"
              className="absolute inset-0 w-full h-full object-cover rounded-md"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-800 rounded-md" />
          )}
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors rounded-md" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-6 h-6 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <video
          className="absolute inset-0 w-full h-full rounded-md bg-black"
          src={videoUrl}
          poster={posterUrl || undefined}
          controls
          autoPlay
          playsInline
          preload="none"
        />
      </div>
    )
  }

  if (!url) return null

  const embedUrl = getEmbedUrl(url)
  if (!embedUrl) return null

  const thumbnailUrl = getYoutubeThumbnail(url)

  if (!active) {
    return (
      <div
        className="relative w-full cursor-pointer group"
        style={{ paddingBottom: '56.25%' }}
        onClick={() => setActive(true)}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt="Vídeo do lote"
            className="absolute inset-0 w-full h-full object-cover rounded-md"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gray-800 rounded-md" />
        )}
        {/* Overlay escuro no hover */}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors rounded-md" />
        {/* Botão play */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 md:w-16 md:h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg className="w-6 h-6 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe
        className="absolute inset-0 w-full h-full rounded-md"
        src={`${embedUrl}&autoplay=1`}
        title="Vídeo do lote"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}

export function getEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null

  const trimmed = url.trim()

  const watchMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/
  )
  if (watchMatch) {
    return `https://www.youtube-nocookie.com/embed/${watchMatch[1]}?playsinline=1&rel=0`
  }

  const shortMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/
  )
  if (shortMatch) {
    return `https://www.youtube-nocookie.com/embed/${shortMatch[1]}?playsinline=1&rel=0`
  }

  const embedMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/
  )
  if (embedMatch) {
    return `https://www.youtube-nocookie.com/embed/${embedMatch[1]}?playsinline=1&rel=0`
  }

  const liveMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]+)/
  )
  if (liveMatch) {
    return `https://www.youtube-nocookie.com/embed/${liveMatch[1]}?playsinline=1&rel=0`
  }

  const vimeoMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/
  )
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  }

  return null
}

export function getYoutubeThumbnail(url) {
  if (!url || typeof url !== 'string') return null

  const trimmed = url.trim()

  const watchMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/
  )
  if (watchMatch) return `https://i.ytimg.com/vi/${watchMatch[1]}/hqdefault.jpg`

  const shortMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/
  )
  if (shortMatch) return `https://i.ytimg.com/vi/${shortMatch[1]}/hqdefault.jpg`

  const embedMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/
  )
  if (embedMatch) return `https://i.ytimg.com/vi/${embedMatch[1]}/hqdefault.jpg`

  const liveMatch = trimmed.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]+)/
  )
  if (liveMatch) return `https://i.ytimg.com/vi/${liveMatch[1]}/hqdefault.jpg`

  return null
}
